import { DraggableHandle, SortableItem, VpdList } from "@design-system";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Typography } from "antd";
import React, { useState } from "react";
import styles from "./hierarchy-container.css";
import { sortBy } from "lodash";

import { mergeArraysOfObjectsByProp } from "../../../helpers/merge-arrays-of-objects-by-prop";

type Props<T extends { id: number; description: string; order: number }> = {
  items: T[];
  onFinished: (items: T[]) => void;
};

export const UnitHierarchies = <T extends { id: number; description: string; order: number }>({
  items,
  onFinished,
}: Props<T>) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const [sortedItems, setItemsOrder] = useState<T[]>(items);

  const onReOrder = (activeId: number, overId: number) => {
    const sortedItemsCopy = sortedItems.map((item) => ({ ...item }));
    const activeIndex = sortedItemsCopy.findIndex((d) => d.id === activeId);
    const activeItem = sortedItemsCopy[activeIndex];
    const overIndex = sortedItemsCopy.findIndex((d) => d.id === overId);

    const updates: T[] = [];
    // Drag Down

    if (activeIndex < overIndex) {
      sortedItemsCopy.forEach((item, index) => {
        if (index <= overIndex && index !== activeIndex) {
          updates.push({ ...item, order: index - 1 });
        }
      });
      updates.push({ ...activeItem, order: overIndex });
    }
    // Drag Up
    else if (activeIndex > overIndex) {
      sortedItemsCopy.forEach((item, index) => {
        if (index >= overIndex && index !== activeIndex) {
          updates.push({ ...item, order: index + 1 });
        }
      });
      updates.push({ ...activeItem, order: overIndex });
    }

    const merged = sortBy(mergeArraysOfObjectsByProp(updates, sortedItemsCopy, "id"), "order");
    setItemsOrder(merged);
    onFinished(updates);
  };

  const content = (
    <div className={styles.tab_content}>
      <DndContext
        onDragEnd={({ active, over }) => over && onReOrder(parseInt(active.id), parseInt(over.id))}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={sortedItems.map((x) => x.id.toString())}
          strategy={verticalListSortingStrategy}
        >
          <VpdList
            items={sortBy(sortedItems, "order")}
            split
            itemKeyProperty="id"
            itemTextProperty="description"
            listStyle="primary"
            renderItem={(item) => (
              <SortableItem id={item.id.toString()} key={item.id}>
                {({ attributes, listeners }) => (
                  <VpdList.Item
                    contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                    key={item.id}
                    content={
                      <div className={styles.order_item}>
                        <Typography.Text editable={false}>{item.description}</Typography.Text>
                      </div>
                    }
                  ></VpdList.Item>
                )}
              </SortableItem>
            )}
          />
        </SortableContext>
      </DndContext>
    </div>
  );

  return content;
};
